/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from '@reduxjs/toolkit';
import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import _ from 'lodash';
import { ComponentProps, memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from 'rsuite';
import { DataItem } from '../../../../../@types/DataItem';
import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { RootState } from '../../../../../@types/RootState';
import { InnerFilter } from '../../../../../components';
import { listFiltrosSalvos } from '../../../../../components/FilterSection/services';
import { getMoreFilterDataItem } from '../../../../../components/NewFilterBox/data/service';
import { resetPriceManagerFilters } from '../../../../../components/NewFilterBox/data/utils';
import { useDebounce } from '../../../../../hooks';
import { SET_FILTER_CACHE_BY_KEY } from '../../../../../reducers/gerenciadorPrecos/filters/filtersCache';
import { SET_FILTER_DATA_BY_KEY, selectorFiltersData } from '../../../../../reducers/gerenciadorPrecos/filters/filtersData';
import {
  ADD_SEGMENT_VALUE,
  SET_FILTERS_VALUE_BY_KEY,
  SET_PRICE_STATUS,
  SegmentoValue,
  selectorFiltersValues,
} from '../../../../../reducers/gerenciadorPrecos/filters/filtersValues';
import {
  ADD_MORE_FILTERS_PILL,
  MoreFiltersDataItem,
  MoreFiltersValue,
  SET_MORE_FILTERS_CACHE_RENDER_BY_KEY,
  SET_MORE_FILTERS_VALUE_RENDER_BY_KEY,
  moreFiltersValue,
  selectorPriceManagerMoreFiltersValues,
} from '../../../../../reducers/gerenciadorPrecos/filters/moreFilters';
import {
  CHANGE_QUICK_FILTER_VALUE,
  ChangeQuickActionFilterValue,
  ChangedCostRequest,
  ChangedCostsFilter,
  CompetitivenessFilterProps,
  MarginFilterProps,
  PriceVariationFilterProps,
  QuickActionFilterLimitsKeys,
  TOGGLE_QUICK_FILTER_ON,
  clampMinPct,
  initialChangedCostsMinPct,
  parseDays,
  setChangedCostsFilter,
  setCompetitivenessFilter,
  setMarginFilter,
  setPriceVariationFilter,
  setQuickActionPriceTypeFilter,
  toggleQuickActionFilter,
} from '../../../../../reducers/gerenciadorPrecos/quickActionsFilter';
import { SET_IS_OPEN as OPEN_DELETE_FILTER_MODAL } from '../../../../../reducers/ipa/priceManager/modals/deleteFilter';
import {
  SET_IS_OPEN as OPEN_SAVE_FILTERS_MODAL,
  SET_DATA,
  SET_DATA_ITEM,
  SET_NAME,
  SET_STATE,
  SaveFilterData,
  SaveFiltersState,
  selectorSaveFilters,
} from '../../../../../reducers/ipa/priceManager/modals/saveFilters';
import { useCompetitorFiltersStore } from '../../../../../store/useCompetitorFiltersStore';
import { useStoreTypeStore } from '../../../../../store/useStoreTypeStore';
import { checkIfIsLoading } from '../../../../../utils';
import { getLimitFilterValueFromParams, isEmptyFilters } from '../../utils';
import { Icon } from '../QuickActionFilter';
import styles from './SavedFilters.module.scss';
import { getSavedMoreFilterCache, getSavedResponsibleFilter, getSavedSegmentValues } from './utils';
import type { SavedResponsible } from './utils';

type Key = GerenciadorPrecos.FilterKeys;

type FilterItem = {
  id: string;
  description: string;
  [key: string]: any;
};

type Filter = {
  user: string | null;
  client: string | null;
  clientId: number | null;
  screen: string | null;
  filters: {
    competitorPriceSources?: string[];
    daysCompetitorsPrice?: number;
    minDateCompetitorPrice?: number;
    responsible?: SavedResponsible[] | null;
    products?: FilterItem[];
    stores?: FilterItem[];
    priceStatusFilter?: {
      key: string;
      status: string[];
    }[];
    [key: string]: any;
  };
  id: string;
  name: string;
  label: string;
  value: string;
};

type SavedFiltersProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & ComponentProps<'div'>;

const SavedFilters = ({
  className,
  filtersValues,
  filtersData,
  moreFilters,
  savedFilters: { dataItem, data, name },
  openSaveFiltersModal,
  openDeleteFilterModal,
  setDataItem,
  setData,
  setName,
  setSavedFilter,
  resetPriceManagerFilters,
  setFilterDataByKey,
  setFilterCacheByKey,
  setFilterValueByKey,
  toggleQuickActionFilter,
  addSegmentValue,
  toggleQuickActionFilterOn,
  changeQuickActionFilterValue,
  setMarginFilter,
  setPriceVariationFilter,
  setCompetitivenessFilter,
  setQuickActionPriceTypeFilter,
  setChangedCostsFilter,
  setPriceStatus,
  addMoreFiltersPill,
  setMoreFiltersValueRenderByKey,
  setMoreFiltersCacheRenderByKey,
  ...props
}: SavedFiltersProps) => {
  const debounce = useDebounce();

  const [search, setSearch] = useState('');

  const deferredSearch = useDeferredValue(search);

  const { addCompetitorFilter, clearCompetitorFilters } = useCompetitorFiltersStore();

  const setSelectedStoreTypes = useStoreTypeStore(state => state.setSelectedStoreTypes);

  const onSearch = useCallback((query: string) => debounce(() => setSearch(query)), [debounce]);

  const onClose = useCallback(() => onSearch(''), [onSearch]);

  const onClean = useCallback(() => {
    resetPriceManagerFilters();
    clearCompetitorFilters();
    setSelectedStoreTypes([]);
  }, [clearCompetitorFilters, resetPriceManagerFilters, setSelectedStoreTypes]);

  const handleSetFilter = useCallback(
    ({ key, value = [], cache = [] }: { key: GerenciadorPrecos.FilterKeys; value: string[]; cache: DataItem[] }) => {
      const uniqDataWithCache = _.uniqBy([...cache, ...(filtersData[key] ?? [])], 'value');
      setFilterDataByKey(key, uniqDataWithCache);
      setFilterCacheByKey(key, cache);
      setFilterValueByKey(key, value);
    },
    [setFilterDataByKey, setFilterCacheByKey, setFilterValueByKey, filtersData],
  );

  const onSelect = useCallback(
    ({ id, name, filters }: Filter) => {
      onClean();

      setName(name);

      setData({ name, label: name, value: id });

      Object.keys(filters).forEach(key => {
        if (filters[key as keyof typeof filters] === null) return;

        if (['competitorPrices', 'minDateCompetitorPrice', 'competitorPriceSources', 'daysCompetitorsPrice'].includes(key)) {
          const hasCompetitorValues =
            (filters.competitorPriceSources && filters.competitorPriceSources.length > 0) ||
            (filters.daysCompetitorsPrice && Number(filters.daysCompetitorsPrice) > 0) ||
            (filters.minDateCompetitorPrice && Number(filters.minDateCompetitorPrice) > 0);

          addCompetitorFilter({
            filterType: key,
            filterValue: hasCompetitorValues ? (key === 'competitorPrices' ? true : filters[key as keyof typeof filters]) : null,
          });
        }
        if (key === 'storeType') {
          setSelectedStoreTypes(filters[key]);
        }
        switch (key) {
          case 'isProductMaster': {
            toggleQuickActionFilter(key);
            break;
          }
          case 'productIds': {
            const cache =
              filters.products?.map(({ id, description, ...filtro }) => ({
                label: `${id} - ${description}`,
                value: id,
                ...filtro,
              })) ?? [];
            const value = filters.productIds ?? [];
            handleSetFilter({ key: 'productIds', value, cache });
            break;
          }
          case 'storeIds': {
            const cache =
              filters.stores?.map(({ id, description }) => ({
                label: `${id} - ${description}`,
                value: id,
              })) ?? [];
            const value = filters.storeIds ?? [];
            handleSetFilter({ key: 'storeIds', value, cache });
            break;
          }
          case 'productFamilyIds': {
            const cache =
              filters[key]?.map((item: any) => ({
                label: item,
                value: item,
              })) ?? [];
            const value = filters[key] ?? [];
            handleSetFilter({
              key: 'productFamilyIds',
              value,
              cache,
            });
            break;
          }
          case 'storeType': {
            const cache =
              filters[key]?.map((item: any) => ({
                label: item,
                value: item,
              })) ?? [];
            const value = filters.storeType ?? [];
            handleSetFilter({ key: 'storeType', value, cache });
            break;
          }
          case 'responsible': {
            const { value, cache } = getSavedResponsibleFilter(filters[key]);
            handleSetFilter({ key: 'responsible', value, cache });
            break;
          }
          case 'supplier': {
            const cache =
              filters[key]?.map((item: any) => ({
                label: item,
                value: item,
              })) ?? [];
            const value = filters.supplier ?? [];
            handleSetFilter({ key: 'supplier', value, cache });
            break;
          }
          case 'productBrand': {
            const cache =
              filters[key]?.map((item: any) => ({
                label: item,
                value: item,
              })) ?? [];
            const value = filters.productBrand ?? [];
            handleSetFilter({ key: 'productBrand', value, cache });
            break;
          }
          case 'sensibilities':
          case 'exclusive':
          case 'curveAbcLocal':
          case 'curveAbcGlobal':
          case 'curve': {
            addSegmentValue(getSavedSegmentValues(key, filters[key]));
            break;
          }
          case 'categoryLevel1':
          case 'categoryLevel2':
          case 'categoryLevel3':
          case 'categoryLevel4':
          case 'categoryLevel5':
          case 'categoryLevel6':
          case 'categoryLevel7': {
            const cache =
              filters[key]?.map((item: any) => ({
                label: item,
                value: item,
              })) ?? [];
            const value = filters[key] ?? [];
            handleSetFilter({ key, value, cache });
            break;
          }
          case 'changedPrices': {
            if (filters.changedPrices) {
              toggleQuickActionFilterOn(key);
            }
            break;
          }
          case 'brokenLimit':
          case 'adjustedPriceByLimit': {
            const limitFilterValue = getLimitFilterValueFromParams({
              brokenLimit: filters.brokenLimit,
              adjustedPriceByLimit: filters.adjustedPriceByLimit,
            });
            if (limitFilterValue === null) break;
            toggleQuickActionFilterOn('brokenLimit');
            changeQuickActionFilterValue('brokenLimit', limitFilterValue);
            break;
          }
          case 'marginRange': {
            const marginFilter = filters[key] as Omit<MarginFilterProps, 'isActive'>;
            toggleQuickActionFilterOn(key);
            setMarginFilter({ ...marginFilter, isActive: true });
            break;
          }
          case 'priceVariationRange': {
            const priceVariationFilter = filters[key] as Omit<PriceVariationFilterProps, 'isActive'>;
            toggleQuickActionFilterOn(key);
            setPriceVariationFilter({ ...priceVariationFilter, isActive: true });
            break;
          }
          case 'cpiRange': {
            const competitivenessFilter = filters[key] as Omit<CompetitivenessFilterProps, 'isActive'>;
            toggleQuickActionFilterOn(key);
            setCompetitivenessFilter({ ...competitivenessFilter, isActive: true });
            break;
          }
          case 'priceTypeFilter': {
            setQuickActionPriceTypeFilter(filters[key] === 'OFFER' ? 'OFFER' : 'REGULAR');
            break;
          }
          case 'changedCost': {
            const changedCost = filters[key] as ChangedCostRequest | undefined;
            if (!changedCost) break;

            setChangedCostsFilter({
              isActive: true,
              value: parseDays(changedCost.costChangeDays),
              minPct: clampMinPct(Number(changedCost.costChangeMinPct)),
              directionUp: changedCost.directionUp ?? true,
              directionDown: changedCost.directionDown ?? true,
            });
            break;
          }
          // Compatibilidade com filtros salvos antes do formato `changedCost`, que guardavam apenas o número de dias.
          case 'changedCosts': {
            const legacyDays = Number(filters[key]);
            if (isNaN(legacyDays)) break;

            setChangedCostsFilter({
              isActive: true,
              value: legacyDays,
              minPct: initialChangedCostsMinPct,
              directionUp: true,
              directionDown: true,
            });
            break;
          }
          case 'informationClusters': {
            const currentClusterIds = filters[key] ?? [];

            const value: string[] = [];
            const cache: DataItem[] = [];

            currentClusterIds.forEach(({ id, description }: { id: string; description: string }) => {
              value.push(id);
              cache.push({
                label: description,
                value: id,
              });
            });

            handleSetFilter({ key: 'clusters', value, cache });
            break;
          }
          case 'daysCompetitorsPrice': {
            const daysValue = Number(filters[key]);
            if (!isNaN(daysValue)) {
              changeQuickActionFilterValue('minDateCompetitorPrice', daysValue);
              toggleQuickActionFilterOn('minDateCompetitorPrice');
            }
            break;
          }
          case 'priceStatusFilter': {
            const currentFilter = filters?.priceStatusFilter ?? [];
            const value = currentFilter.flatMap(({ key, status }) => {
              return status?.map(value => ({
                type: key,
                value,
              }));
            });
            setPriceStatus(value);
            break;
          }
          default:
            break;
        }

        if (moreFiltersValue.includes(key as MoreFiltersValue)) {
          const _key = key as MoreFiltersValue;
          const pillDataItem = getMoreFilterDataItem(_key);
          if (!pillDataItem) return;
          const value = filters[_key] ?? [];
          const cache = getSavedMoreFilterCache(_key, filters[_key]);
          addMoreFiltersPill(pillDataItem);
          setMoreFiltersValueRenderByKey(_key, value);
          setMoreFiltersCacheRenderByKey(_key, cache);
        }
      });
    },
    [
      onClean,
      setName,
      setData,
      addCompetitorFilter,
      setSelectedStoreTypes,
      toggleQuickActionFilter,
      handleSetFilter,
      addSegmentValue,
      toggleQuickActionFilterOn,
      changeQuickActionFilterValue,
      setMarginFilter,
      setPriceVariationFilter,
      setCompetitivenessFilter,
      setQuickActionPriceTypeFilter,
      setChangedCostsFilter,
      setPriceStatus,
      addMoreFiltersPill,
      setMoreFiltersValueRenderByKey,
      setMoreFiltersCacheRenderByKey,
    ],
  );

  const isEmpty = useMemo(() => isEmptyFilters({ ...filtersValues, ...moreFilters }), [filtersValues, moreFilters]);

  const { fetchStatus } = useQuery({
    queryKey: ['ipa.filter.savedFilters', deferredSearch],
    queryFn: () => listFiltrosSalvos(deferredSearch),
    initialData: [],
    onSuccess: setDataItem,
  });

  useEffect(() => {
    const filtroSalvo = dataItem.find(item => item.label === name);
    if (filtroSalvo) {
      const { label, value } = filtroSalvo;
      setSavedFilter({ data: { name: label, value, label } });
    }
  }, [dataItem, name, setSavedFilter]);

  return (
    <div className={cn(styles.wrapper, className)} {...props}>
      {!isEmpty && (
        <Button className={styles.button} onClick={openSaveFiltersModal}>
          <Icon icon="MdSave" />
        </Button>
      )}
      <InnerFilter
        data={dataItem ?? []}
        value={data?.value ?? ''}
        type="select"
        placeholder="Filtros salvos"
        placement="bottomEnd"
        onClose={onClose}
        onSearch={onSearch}
        onClean={onClean}
        onSelect={(_v: unknown, item: unknown) => onSelect(item as Filter)}
        isLoading={checkIfIsLoading(fetchStatus)}
      />
      {!!data?.value.length && (
        <Button className={styles.button} onClick={openDeleteFilterModal}>
          <Icon icon="MdDelete" />
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  filtersValues: selectorFiltersValues(state),
  filtersData: selectorFiltersData(state),
  savedFilters: selectorSaveFilters(state),
  moreFilters: selectorPriceManagerMoreFiltersValues(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  // Save Filters Actions
  setData: (data: SaveFilterData) => dispatch(SET_DATA(data)),
  setName: (name: string) => dispatch(SET_NAME(name)),
  setDataItem: (data: DataItem[]) => dispatch(SET_DATA_ITEM(data)),
  openSaveFiltersModal: () => dispatch(OPEN_SAVE_FILTERS_MODAL(true)),
  setSavedFilter: (data: Partial<SaveFiltersState>) => dispatch(SET_STATE(data)),
  // Delete Filters Actions
  openDeleteFilterModal: () => dispatch(OPEN_DELETE_FILTER_MODAL(true)),
  // Price Manager Filters Actions
  resetPriceManagerFilters: () => resetPriceManagerFilters(dispatch),
  setFilterDataByKey: (key: Key, data: DataItem[]) => dispatch(SET_FILTER_DATA_BY_KEY({ key, data })),
  setFilterCacheByKey: (key: Key, cache: DataItem[]) => dispatch(SET_FILTER_CACHE_BY_KEY({ key, cache })),
  setFilterValueByKey: (key: Key, value: string[]) => dispatch(SET_FILTERS_VALUE_BY_KEY({ key, value })),
  toggleQuickActionFilter: (key: QuickActionFilterLimitsKeys) => dispatch(toggleQuickActionFilter({ key })),
  addSegmentValue: (segment: SegmentoValue[]) => dispatch(ADD_SEGMENT_VALUE(segment)),
  toggleQuickActionFilterOn: (key: QuickActionFilterLimitsKeys) => dispatch(TOGGLE_QUICK_FILTER_ON(key)),
  changeQuickActionFilterValue: (name: ChangeQuickActionFilterValue['name'], value: ChangeQuickActionFilterValue['value']) =>
    dispatch(CHANGE_QUICK_FILTER_VALUE({ name, value })),
  setMarginFilter: (data: MarginFilterProps) => dispatch(setMarginFilter(data)),
  setPriceVariationFilter: (data: PriceVariationFilterProps) => dispatch(setPriceVariationFilter(data)),
  setCompetitivenessFilter: (data: CompetitivenessFilterProps) => dispatch(setCompetitivenessFilter(data)),
  setQuickActionPriceTypeFilter: (value: 'REGULAR' | 'OFFER') => dispatch(setQuickActionPriceTypeFilter(value)),
  setChangedCostsFilter: (data: ChangedCostsFilter) => dispatch(setChangedCostsFilter(data)),
  setPriceStatus: (data: SegmentoValue[]) => dispatch(SET_PRICE_STATUS(data)),
  addMoreFiltersPill: (data: MoreFiltersDataItem) => dispatch(ADD_MORE_FILTERS_PILL(data)),
  setMoreFiltersValueRenderByKey: (key: MoreFiltersValue, value: string[]) => dispatch(SET_MORE_FILTERS_VALUE_RENDER_BY_KEY({ key, value })),
  setMoreFiltersCacheRenderByKey: (key: MoreFiltersValue, cache: DataItem[]) => dispatch(SET_MORE_FILTERS_CACHE_RENDER_BY_KEY({ key, value: cache })),
});

export default memo(connect(mapStateToProps, mapDispatchToProps)(SavedFilters));
